import { Spinner } from "@climatepartner/components/Spinner";
import { InvitationScreen } from "@service/network/supplier/dto/resolve-invite-response.dto";
import { ReactElement } from "react";
import { useSearchParams } from "react-router-dom";

import { formatLongDateOrPlaceholder } from "../../../../../shared/lib/format-date";
import { useResolveInvite } from "../api/use-resolve-invite";
import { ErrorCard } from "./error-card";
import { toErrorCardVariant } from "./error-card.helpers";
import { ForwardForm } from "./forward-form";
import { BUYER_NAME_FALLBACK } from "./invitation-header-card";
import { PageChrome } from "./page-chrome";

// Reuses the same public resolve endpoint as SupplierLandingPage purely to source the invitation
// header (buyerName, respondBy) shown above the form — the `screen` field it also returns is
// irrelevant here, since forwarding is valid regardless of the invited supplier's registration state.
export const SupplierForwardPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const cr = searchParams.get("cr") ?? "";
  const token = searchParams.get("token") ?? "";

  const { data, isLoading, error } = useResolveInvite(cr, token);

  if (!cr || !token || error) {
    return (
      <PageChrome>
        <ErrorCard variant={toErrorCardVariant(error)} />
      </PageChrome>
    );
  }

  if (isLoading || !data) {
    return (
      <PageChrome>
        <Spinner />
      </PageChrome>
    );
  }

  // A connected row has nothing left to forward; same card the forward link got when this was a 404.
  if (data.screen === InvitationScreen.ALREADY_CONNECTED) {
    return (
      <PageChrome>
        <ErrorCard variant="invalid-invite" />
      </PageChrome>
    );
  }

  return (
    <PageChrome>
      <ForwardForm
        connectionRequestId={cr}
        token={token}
        buyerName={data.invitation.buyerName ?? BUYER_NAME_FALLBACK}
        requestDueDate={formatLongDateOrPlaceholder(data.invitation.respondBy)}
      />
    </PageChrome>
  );
};
